import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridRowModes,
  GridRowModel,
} from '@mui/x-data-grid-pro';
import SvgIcon from '@mui/material/SvgIcon';
import CancelIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import startCase from 'lodash/startCase';
import { useMemo } from 'react';
import { isEqual } from 'lodash';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material';
import Card from '@mui/material/Card';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { useScreenResize } from '@mgm/common/hooks/useResize';
import { MemoizedBaseAvatar } from '@mgm/common/components/base';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { ReactComponent as SaveIcon } from '../../../../static/icons/ic_save.svg';
import { useEditTable } from '../../../../hooks/useTable';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { getActiveStep, getGustoData } from '../../../../store/payroll/selectors';
import { TGustoData } from '../../../../types/payroll.type';
import { IntegrationFooter } from '../../Shared/components';
import { useGetGustoDataAPI, useUpdateGustoDataAPI } from '../hooks/useGustoApi';
import { payrollActions } from '../../../../store/payroll/slice';

const GustoTable = () => {
  const { t } = useTranslation(['inputs', 'common', 'payroll']);
  const { pageHeight, resizableDiv } = useScreenResize(80);
  const gustoData = useAppSelector(getGustoData);
  const activeStep = useAppSelector(getActiveStep);
  const dispatch = useAppDispatch();

  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);

  const { mutateAsync, isLoading } = useUpdateGustoDataAPI();
  useGetGustoDataAPI();

  const {
    handleRowEditStop,
    rowModesModel,
    handleSaveClick,
    handleCancelClick,
    handleRowModesModelChange,
    rows,
    setRows,
  } = useEditTable(gustoData);

  const columns: Array<GridColDef> = [
    {
      field: 'name',
      headerName: t('name'),
      minWidth: 130,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          <MemoizedBaseAvatar avatarFile={row.avatarFile} avatarName={row.name} />
          <Typography variant="caption">
            {startCase(row.name)}
          </Typography>
        </Box>
      ),
      flex: 1,
    },
    {
      field: 'email',
      headerName: t('email'),
      minWidth: 250,
      flex: 1,
    },
    {
      field: 'ssn',
      headerName: 'SSN',
      minWidth: 170,
      flex: 1,
      editable: true,
    },
    {
      field: 'gusto_employee_id',
      headerName: t('gustoEmployeeId', { ns: 'payroll' }),
      minWidth: 130,
      flex: 1,
      editable: true,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('actions', { ns: 'common' }),
      minWidth: 100,
      cellClassName: 'actions',
      getActions: (params) => {
        const { id } = params;
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={id}
              icon={(
                <SvgIcon
                  color="primary"
                  fontSize="small"
                  component={SaveIcon}
                  inheritViewBox
                />
              )}
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              key={id}
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [];
      },
    },
  ];

  const isBtnDisabled = useMemo(() => isEqual(rows, gustoData), [rows]);

  const renderCustomNoRowsOverlay = () => <CustomNoRowsOverlay text={t('itLooksLikeThereAreNoData', { ns: 'common' })} />;

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false } as TGustoData;
    setRows(rows?.map((row) => (row.email === newRow.email ? updatedRow : row)));
    return updatedRow;
  };

  const handleSubmit = async () => {
    if (!isBtnDisabled) {
      const data = {
        integrationData: rows,
      };

      await mutateAsync(data);
    }
    dispatch(payrollActions.setActiveStep(activeStep + 1));
  };

  return (
    <div ref={resizableDiv}>
      <Card
        className="overflow-auto"
        sx={{
          height: pageHeight !== 0 ? pageHeight : 'auto',
          ...(isMobile && {
            background: 'unset', boxShadow: 'unset', px: 0, pt: 0, pb: '35px',
          }),
        }}
      >
        <DataGridPro
          rows={rows}
          columns={columns}
          rowHeight={50}
          editMode="row"
          getRowId={(row) => row.email}
          rowModesModel={rowModesModel}
          hideFooter
          onRowModesModelChange={handleRowModesModelChange}
          loading={!gustoData.length}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          slots={{
            noRowsOverlay: renderCustomNoRowsOverlay,
          }}
        />
      </Card>
      {!isMobile && (
      <IntegrationFooter
        nextBtnText={isBtnDisabled ? 'next' : 'saveChanges'}
        isLoading={isLoading}
        integrationName="Gusto"
        handleSubmit={handleSubmit}
      />
      )}
    </div>
  );
};

export {
  GustoTable,
};
